import React from 'react';
import { Box, Button, Slide, Snackbar, Stack, Typography } from '@mui/material';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';

/**
 * Non-blocking "update available" notification surfaced when the version
 * detection hook flips `newVersionAvailable` on (but before the 10-min
 * escalation kicks in and replaces this with a forced dialog).
 *
 * Bottom-center on mobile, bottom-right on desktop. No autoHide — the user
 * either refreshes or waits for the escalation. The snackbar itself does
 * not block any interaction so a user mid-form keeps going until ready.
 */
export default function NewVersionSnackbar({ open, onRefresh }) {
  const handleRefresh = () => {
    if (onRefresh) onRefresh();
    else window.location.reload();
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up' }}
      sx={{
        // Keep the snackbar above the iOS safe-area inset on installed PWAs.
        bottom: { xs: 'calc(16px + env(safe-area-inset-bottom, 0px))', sm: 24 },
        left: { xs: 8, sm: 'auto' },
        right: { xs: 8, sm: 24 },
        zIndex: (t) => t.zIndex.snackbar,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          minWidth: 280,
          maxWidth: { xs: '100%', sm: 420 },
          p: 1.25,
          pl: 1.5,
          borderRadius: 2.5,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'primary.light',
          boxShadow:
            '0 10px 30px -10px rgba(15, 23, 42, 0.18), 0 4px 12px -4px rgba(15, 23, 42, 0.10)',
        }}
      >
        <Box
          sx={{
            width: 36, height: 36, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'primary.main', color: 'primary.contrastText',
            flexShrink: 0,
          }}
        >
          <RocketLaunchRoundedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Mise à jour disponible
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>
            Rechargez quand vous serez prêt. Vos données restent enregistrées.
          </Typography>
        </Stack>
        <Button
          variant="contained"
          size="small"
          onClick={handleRefresh}
          startIcon={<AutorenewRoundedIcon sx={{ fontSize: 16 }} />}
          sx={{ flexShrink: 0, fontWeight: 600 }}
        >
          Recharger
        </Button>
      </Box>
    </Snackbar>
  );
}
